import {
  CaptureUpdateAction,
  Excalidraw,
  convertToExcalidrawElements,
  exportToBlob,
  exportToSvg,
} from "@excalidraw/excalidraw";
import "@excalidraw/excalidraw/index.css";
import type {
  AppState,
  BinaryFileData,
  BinaryFiles,
  ExcalidrawImperativeAPI,
  ExcalidrawInitialDataState,
} from "@excalidraw/excalidraw/types";
import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";
import {
  ArrowDownToLine,
  ArrowRight,
  Circle,
  Diamond,
  Download,
  Expand,
  FileJson,
  Focus,
  Fullscreen,
  Hand,
  HelpCircle,
  LayoutGrid,
  Maximize2,
  Menu,
  Minus,
  MousePointer2,
  Pencil,
  Plus,
  Redo2,
  RectangleHorizontal,
  RotateCcw,
  Save,
  StickyNote,
  TextCursorInput,
  Undo2,
  Upload,
  Video,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BoardManager } from "./components/BoardManager";
import { Modal } from "./components/Modal";
import { TemplateGallery } from "./components/TemplateGallery";
import { downloadBlob, projectBlob, safeFilename } from "./lib/download";
import { createId } from "./lib/ids";
import { InvalidProjectError, MAX_IMAGE_BYTES, readProjectFile } from "./lib/importValidation";
import { cloneBoard, createBoard, sceneFromExcalidraw } from "./lib/scene";
import { boardStorage } from "./lib/storage";
import { STARTER_TEMPLATES } from "./lib/templates";
import type { BoardRecord, SaveStatus, SceneData, StarterTemplate, ToastMessage } from "./types";

const SAVE_DELAY_MS = 450;
const ALLOWED_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/webp", "image/gif"]);

type StandardTool = "selection" | "text" | "rectangle" | "ellipse" | "diamond" | "freedraw" | "line" | "arrow" | "hand";

interface SafeClipboardData {
  elements?: readonly ExcalidrawElement[];
  files?: BinaryFiles;
}

interface ToolItem {
  id: StandardTool | "rounded" | "sticky";
  label: string;
  shortcut?: string;
  icon: typeof MousePointer2;
  separated?: boolean;
}

const TOOLS: readonly ToolItem[] = [
  { id: "selection", label: "Pilih", shortcut: "V / 1", icon: MousePointer2 },
  { id: "hand", label: "Gerakkan kanvas", shortcut: "H", icon: Hand },
  { id: "text", label: "Teks", shortcut: "T / 8", icon: TextCursorInput, separated: true },
  { id: "rectangle", label: "Segi empat", shortcut: "R / 2", icon: RectangleHorizontal },
  { id: "rounded", label: "Segi empat bulat", icon: Expand },
  { id: "ellipse", label: "Bulatan", shortcut: "O / 4", icon: Circle },
  { id: "diamond", label: "Berlian", shortcut: "D / 3", icon: Diamond },
  { id: "sticky", label: "Nota lekit", icon: StickyNote },
  { id: "freedraw", label: "Lukisan bebas", shortcut: "P / 7", icon: Pencil, separated: true },
  { id: "line", label: "Garisan", shortcut: "L / 6", icon: Minus },
  { id: "arrow", label: "Anak panah", shortcut: "A / 5", icon: ArrowRight },
];

function cloneScene(scene: SceneData): ExcalidrawInitialDataState {
  return {
    elements: scene.elements as readonly ExcalidrawElement[],
    appState: scene.appState as Partial<AppState>,
    files: scene.files as BinaryFiles,
    scrollToContent: scene.elements.length > 0,
  };
}

function formatError(error: unknown): string {
  if (error instanceof InvalidProjectError) return error.message;
  if (error instanceof Error) return error.message;
  return "Tindakan tidak dapat diselesaikan.";
}

function byteEstimate(dataUrl: string): number {
  const comma = dataUrl.indexOf(",");
  return comma === -1 ? Number.POSITIVE_INFINITY : Math.ceil((dataUrl.length - comma - 1) * 0.75);
}

function ToolRail({ activeTool, onTool }: { activeTool: string; onTool: (tool: ToolItem["id"]) => void }) {
  return (
    <aside aria-label="Alat lukisan" className="tool-rail">
      {TOOLS.map(({ id, label, shortcut, icon: Icon, separated }) => (
        <button
          aria-label={`${label}${shortcut ? ` (${shortcut})` : ""}`}
          className={`tool-button${activeTool === id ? " tool-button--active" : ""}${separated ? " tool-button--separated" : ""}`}
          data-tooltip={`${label}${shortcut ? ` · ${shortcut}` : ""}`}
          key={id}
          onClick={() => onTool(id)}
          type="button"
        >
          <Icon size={20} strokeWidth={1.8} />
        </button>
      ))}
    </aside>
  );
}

function SaveIndicator({ status, fallback }: { status: SaveStatus; fallback: boolean }) {
  const copy = {
    idle: "Sedia",
    saving: "Menyimpan…",
    saved: fallback ? "Disimpan (sandaran)" : "Disimpan setempat",
    error: "Gagal menyimpan",
  }[status];
  return (
    <span className={`save-indicator save-indicator--${status}`} title="Data tidak dihantar ke pelayan">
      <span aria-hidden="true" /> {copy}
    </span>
  );
}

export default function App() {
  const [boards, setBoards] = useState<BoardRecord[]>([]);
  const [currentBoard, setCurrentBoard] = useState<BoardRecord>();
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [activeTool, setActiveTool] = useState("selection");
  const [zoom, setZoom] = useState(100);
  const [isRecording, setIsRecording] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
  const [showBoards, setShowBoards] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const apiRef = useRef<ExcalidrawImperativeAPI>();
  const currentBoardRef = useRef<BoardRecord>();
  const saveTimerRef = useRef<number>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addToast = useCallback((message: string, tone: ToastMessage["tone"] = "info") => {
    const toast = { id: createId("toast"), message, tone };
    setToasts((items) => [...items.slice(-2), toast]);
    window.setTimeout(() => setToasts((items) => items.filter((item) => item.id !== toast.id)), 3_800);
  }, []);

  const updateBoardInState = useCallback((updated: BoardRecord) => {
    setBoards((items) => {
      const exists = items.some((item) => item.id === updated.id);
      return exists ? items.map((item) => (item.id === updated.id ? updated : item)) : [updated, ...items];
    });
  }, []);

  const persistCurrent = useCallback(async () => {
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    const board = currentBoardRef.current;
    if (!board) return;
    setSaveStatus("saving");
    try {
      await boardStorage.saveBoard(board);
      updateBoardInState(board);
      setSaveStatus("saved");
    } catch (error) {
      setSaveStatus("error");
      addToast(`Simpanan gagal: ${formatError(error)}`, "error");
    }
  }, [addToast, updateBoardInState]);

  useEffect(() => {
    let mounted = true;
    const bootstrap = async () => {
      try {
        let stored = await boardStorage.listBoards();
        if (stored.length === 0) {
          const first = createBoard("Papan Pertama");
          await boardStorage.saveBoard(first);
          stored = [first];
        }
        const activeId = await boardStorage.getActiveBoardId();
        const active = stored.find((board) => board.id === activeId) ?? stored[0];
        if (!mounted) return;
        setBoards(stored);
        setCurrentBoard(active);
        currentBoardRef.current = active;
        setSaveStatus("saved");
        await boardStorage.setActiveBoardId(active.id);
        if (boardStorage.isUsingFallback) {
          addToast("IndexedDB tidak tersedia. Sandaran localStorage sedang digunakan.", "warning");
        }
      } catch (error) {
        const emergency = createBoard("Papan Sementara");
        if (!mounted) return;
        setBoards([emergency]);
        setCurrentBoard(emergency);
        currentBoardRef.current = emergency;
        setSaveStatus("error");
        addToast(`Storan pelayar gagal: ${formatError(error)}`, "error");
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    void bootstrap();
    return () => {
      mounted = false;
    };
  }, [addToast]);

  useEffect(() => {
    const flushWhenHidden = () => {
      if (document.visibilityState === "hidden") void persistCurrent();
    };
    const trackFullscreen = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("visibilitychange", flushWhenHidden);
    document.addEventListener("fullscreenchange", trackFullscreen);
    return () => {
      document.removeEventListener("visibilitychange", flushWhenHidden);
      document.removeEventListener("fullscreenchange", trackFullscreen);
    };
  }, [persistCurrent]);

  const handleSceneChange = useCallback(
    (elements: readonly ExcalidrawElement[], appState: AppState, files: BinaryFiles) => {
      const board = currentBoardRef.current;
      if (!board) return;
      const nextBoard: BoardRecord = {
        ...board,
        updatedAt: new Date().toISOString(),
        scene: sceneFromExcalidraw(
          elements,
          appState as unknown as Record<string, unknown>,
          files as unknown as Record<string, unknown>,
        ),
      };
      currentBoardRef.current = nextBoard;
      setActiveTool(appState.activeTool.type);
      const nextZoom = Math.round(appState.zoom.value * 100);
      setZoom((value) => (value === nextZoom ? value : nextZoom));
      setSaveStatus("saving");
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = window.setTimeout(() => void persistCurrent(), SAVE_DELAY_MS);
    },
    [persistCurrent],
  );

  const chooseBoard = useCallback(
    async (id: string) => {
      if (id === currentBoardRef.current?.id) {
        setShowBoards(false);
        return;
      }
      await persistCurrent();
      const target = boards.find((board) => board.id === id) ?? (await boardStorage.getBoard(id));
      if (!target) return;
      currentBoardRef.current = target;
      setCurrentBoard(target);
      setActiveTool("selection");
      setShowBoards(false);
      await boardStorage.setActiveBoardId(target.id);
    },
    [boards, persistCurrent],
  );

  const createNewBoard = useCallback(
    async (name = "Papan Tanpa Tajuk") => {
      await persistCurrent();
      const board = createBoard(name);
      await boardStorage.saveBoard(board);
      await boardStorage.setActiveBoardId(board.id);
      currentBoardRef.current = board;
      setCurrentBoard(board);
      updateBoardInState(board);
      setShowBoards(false);
      addToast("Papan baharu dicipta.", "success");
    },
    [addToast, persistCurrent, updateBoardInState],
  );

  const renameBoard = useCallback(
    async (id: string, name: string) => {
      const board = boards.find((item) => item.id === id);
      if (!board || !name.trim()) return;
      const updated = { ...board, name: name.trim().slice(0, 80), updatedAt: new Date().toISOString() };
      await boardStorage.saveBoard(updated);
      updateBoardInState(updated);
      if (currentBoardRef.current?.id === id) {
        const active = { ...currentBoardRef.current, name: updated.name, updatedAt: updated.updatedAt };
        currentBoardRef.current = active;
        setCurrentBoard(active);
      }
      addToast("Nama papan dikemas kini.", "success");
    },
    [addToast, boards, updateBoardInState],
  );

  const duplicateBoard = useCallback(
    async (id: string) => {
      if (id === currentBoardRef.current?.id) await persistCurrent();
      const source = id === currentBoardRef.current?.id ? currentBoardRef.current : boards.find((item) => item.id === id);
      if (!source) return;
      const duplicate = cloneBoard(source);
      await boardStorage.saveBoard(duplicate);
      updateBoardInState(duplicate);
      addToast("Salinan papan dicipta.", "success");
    },
    [addToast, boards, persistCurrent, updateBoardInState],
  );

  const deleteBoard = useCallback(
    async (id: string) => {
      if (boards.length <= 1) return;
      const target = boards.find((item) => item.id === id);
      if (!target || !window.confirm(`Padam “${target.name}”? Tindakan ini tidak boleh dibatalkan.`)) return;
      await boardStorage.deleteBoard(id);
      const remaining = boards.filter((item) => item.id !== id);
      setBoards(remaining);
      if (currentBoardRef.current?.id === id) {
        const next = remaining[0];
        currentBoardRef.current = next;
        setCurrentBoard(next);
        await boardStorage.setActiveBoardId(next.id);
      }
      addToast("Papan dipadam.", "success");
    },
    [addToast, boards],
  );

  const chooseTemplate = useCallback(
    async (template: StarterTemplate) => {
      try {
        await persistCurrent();
        const elements = convertToExcalidrawElements(template.specs as never[], { regenerateIds: false });
        const board = createBoard(template.name, {
          elements,
          appState: {
            viewBackgroundColor: "#f8fafc",
            scrollX: 0,
            scrollY: 0,
            zoom: { value: 0.85 },
            theme: "light",
          },
          files: {},
        });
        board.templateId = template.id;
        await boardStorage.saveBoard(board);
        await boardStorage.setActiveBoardId(board.id);
        currentBoardRef.current = board;
        setCurrentBoard(board);
        updateBoardInState(board);
        setShowTemplates(false);
        addToast(`Templat “${template.name}” dibuka.`, "success");
      } catch (error) {
        addToast(`Templat gagal dibuka: ${formatError(error)}`, "error");
      }
    },
    [addToast, persistCurrent, updateBoardInState],
  );

  const selectTool = useCallback(
    (tool: ToolItem["id"]) => {
      const api = apiRef.current;
      if (!api) return;
      if (tool !== "rounded" && tool !== "sticky") {
        api.setActiveTool({ type: tool });
        setActiveTool(tool);
        return;
      }

      const appState = api.getAppState();
      const value = appState.zoom.value;
      const x = appState.width / (2 * value) - appState.scrollX - 100;
      const y = appState.height / (2 * value) - appState.scrollY - 65;
      const id = createId(tool);
      const newElements = convertToExcalidrawElements(
        [
          {
            id,
            type: "rectangle",
            x,
            y,
            width: tool === "sticky" ? 220 : 240,
            height: tool === "sticky" ? 170 : 130,
            roundness: { type: 3 },
            backgroundColor: tool === "sticky" ? "#fef3c7" : "#ffffff",
            strokeColor: tool === "sticky" ? "#d97706" : "#172b4d",
            fillStyle: "solid",
            strokeWidth: 2,
            label: {
              text: tool === "sticky" ? "Nota baharu" : "Tajuk",
              fontSize: tool === "sticky" ? 20 : 18,
            },
          },
        ],
        { regenerateIds: false },
      );
      api.updateScene({
        elements: [...api.getSceneElements(), ...newElements],
        appState: {
          selectedElementIds: Object.fromEntries(newElements.map((element) => [element.id, true])),
        },
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
      api.setActiveTool({ type: "selection" });
      setActiveTool(tool);
    },
    [],
  );

  const dispatchHistoryShortcut = useCallback((redo = false) => {
    const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
    document.dispatchEvent(
      new KeyboardEvent("keydown", {
        key: "z",
        code: "KeyZ",
        ctrlKey: !isMac,
        metaKey: isMac,
        shiftKey: redo,
        bubbles: true,
        cancelable: true,
      }),
    );
  }, []);

  const fitToScreen = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    const elements = api.getSceneElements();
    if (elements.length === 0) {
      api.updateScene({ appState: { scrollX: 0, scrollY: 0, zoom: { value: 1 } as AppState["zoom"] } });
      return;
    }
    api.scrollToContent(elements, { fitToViewport: true, viewportZoomFactor: 0.82, animate: true });
  }, []);

  const setCanvasZoom = useCallback((next: number) => {
    const api = apiRef.current;
    if (!api) return;
    const clamped = Math.max(10, Math.min(400, next));
    api.updateScene({ appState: { zoom: { value: clamped / 100 } as AppState["zoom"] } });
  }, []);

  const resetView = useCallback(() => {
    apiRef.current?.updateScene({
      appState: { scrollX: 0, scrollY: 0, zoom: { value: 1 } as AppState["zoom"] },
    });
  }, []);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch (error) {
      addToast(`Skrin penuh tidak tersedia: ${formatError(error)}`, "warning");
    }
  }, [addToast]);

  const toggleRecording = useCallback(() => {
    setIsRecording((value) => !value);
    setShowExport(false);
  }, []);

  const exportScene = useCallback(
    async (format: "png" | "svg" | "json") => {
      const api = apiRef.current;
      const board = currentBoardRef.current;
      if (!api || !board) return;
      setShowExport(false);
      try {
        const snapshot: BoardRecord = {
          ...board,
          updatedAt: new Date().toISOString(),
          scene: sceneFromExcalidraw(
            api.getSceneElements(),
            api.getAppState() as unknown as Record<string, unknown>,
            api.getFiles() as unknown as Record<string, unknown>,
          ),
        };
        currentBoardRef.current = snapshot;
        await boardStorage.saveBoard(snapshot);
        const filename = safeFilename(snapshot.name);
        if (format === "json") {
          downloadBlob(projectBlob(snapshot), `${filename}.funnelboard.json`);
        } else {
          const elements = api.getSceneElements().filter((element) => !element.isDeleted) as never[];
          if (elements.length === 0) throw new Error("Kanvas masih kosong.");
          const appState = {
            ...api.getAppState(),
            exportBackground: true,
            exportWithDarkMode: false,
            viewBackgroundColor: "#f8fafc",
          };
          if (format === "png") {
            const blob = await exportToBlob({
              elements,
              appState,
              files: api.getFiles(),
              mimeType: "image/png",
              exportPadding: 48,
              getDimensions: (width: number, height: number) => ({ width: width * 2, height: height * 2, scale: 2 }),
            });
            downloadBlob(blob, `${filename}.png`);
          } else {
            const svg = await exportToSvg({ elements, appState, files: api.getFiles(), exportPadding: 48 });
            const markup = new XMLSerializer().serializeToString(svg);
            downloadBlob(new Blob([markup], { type: "image/svg+xml" }), `${filename}.svg`);
          }
        }
        addToast(`Eksport ${format.toUpperCase()} selesai.`, "success");
      } catch (error) {
        addToast(`Eksport gagal: ${formatError(error)}`, "error");
      }
    },
    [addToast],
  );

  const importProject = useCallback(
    async (file: File) => {
      try {
        const project = await readProjectFile(file);
        await persistCurrent();
        const now = new Date().toISOString();
        const imported: BoardRecord = {
          ...project.board,
          id: createId("board"),
          name: `${project.board.name} — Import`.slice(0, 80),
          createdAt: now,
          updatedAt: now,
        };
        await boardStorage.saveBoard(imported);
        await boardStorage.setActiveBoardId(imported.id);
        currentBoardRef.current = imported;
        setCurrentBoard(imported);
        updateBoardInState(imported);
        addToast("Projek berjaya diimport sebagai papan baharu.", "success");
      } catch (error) {
        addToast(`Import ditolak: ${formatError(error)}`, "error");
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [addToast, persistCurrent, updateBoardInState],
  );

  const handlePaste = useCallback(
    (data: SafeClipboardData) => {
      if (data.elements?.some((element) => element.type === "embeddable" || element.type === "iframe" || element.link)) {
        addToast("Pautan dan kandungan terbenam tidak dibenarkan.", "warning");
        return false;
      }
      if (data.files) {
        const files = Object.values(data.files) as BinaryFileData[];
        const invalid = files.some(
          (file) => !ALLOWED_IMAGE_TYPES.has(file.mimeType) || byteEstimate(file.dataURL) > MAX_IMAGE_BYTES,
        );
        if (invalid) {
          addToast("Imej ditolak. Gunakan PNG, JPEG, WebP atau GIF sehingga 5 MB.", "warning");
          return false;
        }
      }
      return true;
    },
    [addToast],
  );

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      const modifier = event.ctrlKey || event.metaKey;
      if (modifier && event.shiftKey && event.key.toLowerCase() === "r") {
        event.preventDefault();
        toggleRecording();
      } else if (modifier && event.shiftKey && event.key.toLowerCase() === "n") {
        event.preventDefault();
        void createNewBoard();
      } else if (modifier && event.key.toLowerCase() === "s") {
        event.preventDefault();
        void persistCurrent().then(() => addToast("Papan disimpan.", "success"));
      } else if (event.key === "?" && !event.ctrlKey && !event.metaKey) {
        setShowShortcuts(true);
      } else if (event.key === "Escape" && isRecording) {
        setIsRecording(false);
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [addToast, createNewBoard, isRecording, persistCurrent, toggleRecording]);

  const initialData = useMemo(() => (currentBoard ? cloneScene(currentBoard.scene) : null), [currentBoard]);

  if (isLoading || !currentBoard || !initialData) {
    return (
      <main className="loading-screen">
        <div className="brand-mark brand-mark--large"><i /><i /><i /></div>
        <h1>FunnelBoard</h1>
        <p>Menyiapkan kanvas setempat anda…</p>
        <span className="loading-bar"><i /></span>
      </main>
    );
  }

  return (
    <main className={`app-shell${isRecording ? " app-shell--recording" : ""}`}>
      <header className="topbar">
        <div className="topbar__brand">
          <span className="brand-mark" aria-hidden="true"><i /><i /><i /></span>
          <span className="brand-copy"><strong>FunnelBoard</strong><small>by AzhanLabs</small></span>
        </div>
        <span className="topbar__divider" />
        <button className="board-title" onClick={() => setShowBoards(true)} type="button">
          <Menu size={16} /> <span>{currentBoard.name}</span> <LayoutGrid size={15} />
        </button>
        <SaveIndicator fallback={boardStorage.isUsingFallback} status={saveStatus} />
        <div className="topbar__history">
          <button aria-label="Buat asal" className="icon-button" onClick={() => dispatchHistoryShortcut(false)} title="Buat asal (Ctrl+Z)" type="button"><Undo2 size={17} /></button>
          <button aria-label="Buat semula" className="icon-button" onClick={() => dispatchHistoryShortcut(true)} title="Buat semula (Ctrl+Shift+Z)" type="button"><Redo2 size={17} /></button>
        </div>
        <div className="topbar__actions">
          <button className="button button--ghost" onClick={() => setShowTemplates(true)} type="button"><LayoutGrid size={16} /> Templat</button>
          <button className="button button--record" onClick={toggleRecording} type="button"><Video size={16} /> Mod Rakaman</button>
          <div className="popover-wrap">
            <button className="button button--primary" onClick={() => setShowExport((value) => !value)} type="button"><Download size={16} /> Eksport</button>
            {showExport ? (
              <div className="export-menu">
                <button onClick={() => void exportScene("png")} type="button"><ArrowDownToLine size={17} /><span><strong>Imej PNG</strong><small>2× resolusi, latar cerah</small></span></button>
                <button onClick={() => void exportScene("svg")} type="button"><Maximize2 size={17} /><span><strong>Vektor SVG</strong><small>Sesuai untuk slaid</small></span></button>
                <button onClick={() => void exportScene("json")} type="button"><FileJson size={17} /><span><strong>Projek JSON</strong><small>Sandaran boleh diimport</small></span></button>
                <span />
                <button onClick={() => fileInputRef.current?.click()} type="button"><Upload size={17} /><span><strong>Import projek</strong><small>JSON FunnelBoard sahaja</small></span></button>
              </div>
            ) : null}
          </div>
          <button aria-label="Skrin penuh" className="icon-button icon-button--outlined" onClick={() => void toggleFullscreen()} title="Skrin penuh" type="button">
            {isFullscreen ? <X size={17} /> : <Fullscreen size={17} />}
          </button>
          <button aria-label="Pintasan papan kekunci" className="icon-button" onClick={() => setShowShortcuts(true)} title="Pintasan (?)" type="button"><HelpCircle size={18} /></button>
        </div>
      </header>

      <section className="canvas-stage">
        <div className="funnel-canvas">
          <Excalidraw
            autoFocus
            excalidrawAPI={(api) => {
              apiRef.current = api;
            }}
            gridModeEnabled={false}
            handleKeyboardGlobally
            initialData={initialData}
            key={currentBoard.id}
            langCode="en"
            name={currentBoard.name}
            onChange={handleSceneChange}
            onPaste={handlePaste}
            theme="light"
            UIOptions={{
              canvasActions: {
                changeViewBackgroundColor: false,
                clearCanvas: false,
                export: false,
                loadScene: false,
                saveAsImage: false,
                saveToActiveFile: false,
                toggleTheme: false,
              },
              tools: { image: false },
            }}
            validateEmbeddable={false}
          />
          <span className="dot-grid" aria-hidden="true" />
        </div>
        <ToolRail activeTool={activeTool} onTool={selectTool} />

        <div className="view-controls">
          <button aria-label="Zum keluar" onClick={() => setCanvasZoom(zoom - 10)} title="Zum keluar" type="button"><Minus size={16} /></button>
          <button className="view-controls__zoom" onClick={resetView} title="Tetap semula kepada 100%" type="button">{zoom}%</button>
          <button aria-label="Zum masuk" onClick={() => setCanvasZoom(zoom + 10)} title="Zum masuk" type="button"><Plus size={16} /></button>
          <span />
          <button aria-label="Muat ke skrin" onClick={fitToScreen} title="Muat ke skrin" type="button"><Focus size={17} /></button>
          <button aria-label="Tetap semula pandangan" onClick={resetView} title="Tetap semula pandangan" type="button"><RotateCcw size={16} /></button>
          <button aria-label="Skrin penuh" onClick={() => void toggleFullscreen()} title="Skrin penuh" type="button"><Maximize2 size={17} /></button>
        </div>

        {isRecording ? (
          <button className="recording-exit" onClick={toggleRecording} type="button">
            <span /> RAKAMAN <small>Esc untuk keluar</small> <X size={16} />
          </button>
        ) : null}
      </section>

      <input
        accept="application/json,.json"
        className="visually-hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void importProject(file);
        }}
        ref={fileInputRef}
        type="file"
      />

      {showBoards ? (
        <BoardManager
          activeBoardId={currentBoard.id}
          boards={boards}
          onClose={() => setShowBoards(false)}
          onCreate={(name) => void createNewBoard(name)}
          onDelete={(id) => void deleteBoard(id)}
          onDuplicate={(id) => void duplicateBoard(id)}
          onRename={(id, name) => void renameBoard(id, name)}
          onSelect={(id) => void chooseBoard(id)}
        />
      ) : null}
      {showTemplates ? (
        <TemplateGallery onChoose={(template) => void chooseTemplate(template)} onClose={() => setShowTemplates(false)} templates={STARTER_TEMPLATES} />
      ) : null}
      {showShortcuts ? (
        <Modal description="Pintasan Excalidraw standard turut disokong." onClose={() => setShowShortcuts(false)} title="Pintasan Papan Kekunci">
          <div className="shortcut-list">
            <span><strong>Pilih / Bentuk</strong><kbd>V</kbd> <kbd>R</kbd> <kbd>O</kbd> <kbd>D</kbd></span>
            <span><strong>Teks / Pen / Anak panah</strong><kbd>T</kbd> <kbd>P</kbd> <kbd>A</kbd></span>
            <span><strong>Buat asal / semula</strong><kbd>Ctrl Z</kbd> <kbd>Ctrl ⇧ Z</kbd></span>
            <span><strong>Salin / Tampal / Duplikasi</strong><kbd>Ctrl C</kbd> <kbd>Ctrl V</kbd> <kbd>Ctrl D</kbd></span>
            <span><strong>Padam pilihan</strong><kbd>Delete</kbd></span>
            <span><strong>Simpan sekarang</strong><kbd>Ctrl S</kbd></span>
            <span><strong>Papan baharu</strong><kbd>Ctrl ⇧ N</kbd></span>
            <span><strong>Mod Rakaman</strong><kbd>Ctrl ⇧ R</kbd></span>
          </div>
        </Modal>
      ) : null}

      <div aria-live="polite" className="toast-stack">
        {toasts.map((toast) => <div className={`toast toast--${toast.tone}`} key={toast.id}>{toast.message}</div>)}
      </div>
    </main>
  );
}
